import {
  ArrowRightOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FlagOutlined,
  PaperClipOutlined
} from "@ant-design/icons"
import { Button, Flex, List, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../cards/WorkspaceCardValue.tsx"
import {
  type LeadActivityKind,
  leadRecentActivity
} from "./lead-overview-activity.ts"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"

const { Text } = Typography

/** Purely decorative — a glyph per existing activity kind, nothing new inferred. */
const ACTIVITY_ICON: Record<LeadActivityKind, ReactNode> = {
  created: <FlagOutlined />,
  event: <CalendarOutlined />,
  attachment: <PaperClipOutlined />,
  proposal: <FileTextOutlined />,
  contract: <FileDoneOutlined />
}

interface LeadOverviewActivityCardProps {
  lead: Lead
  onViewAll: () => void
}

function LeadOverviewActivityCard({
  lead,
  onViewAll
}: LeadOverviewActivityCardProps) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const entries = leadRecentActivity(lead)

  return (
    <WorkspaceCard
      cardId={"activity"}
      title={
        <OverviewCardTitle icon={<FieldTimeOutlined />} accent={"blue"}>
          {t("leadWorkspace.cards.activity.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.activity.title")}
      className={"h-full"}
      extra={
        <Button
          size={"small"}
          type={"link"}
          icon={<ArrowRightOutlined />}
          onClick={onViewAll}
        >
          {t("leadWorkspace.cards.activity.viewAll")}
        </Button>
      }
    >
      <WorkspaceCardValue
        value={entries.length || null}
        placeholder={t("leadWorkspace.cards.activity.empty")}
      >
        <List
          size={"small"}
          dataSource={entries}
          renderItem={(entry, index) => (
            <List.Item key={entry.id} className={"relative !border-b-0 !py-2"}>
              {index < entries.length - 1 && (
                <div
                  aria-hidden
                  className={"absolute left-[13px] top-7 bottom-[-8px] w-px bg-border"}
                />
              )}
              <Flex gap={"small"} align={"start"} className={"w-full min-w-0"}>
                <Flex
                  align={"center"}
                  justify={"center"}
                  className={
                    "w-[26px] h-[26px] rounded-full shrink-0 bg-muted text-primary text-xs"
                  }
                >
                  {ACTIVITY_ICON[entry.kind]}
                </Flex>
                <Flex vertical gap={0} className={"w-full min-w-0"}>
                  <Text ellipsis>{t(entry.labelKey, entry.labelValues)}</Text>
                  <Text type={"secondary"} className={"text-xs"}>
                    {[entry.at ? formatDate(entry.at) : null, entry.actor]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </Flex>
              </Flex>
            </List.Item>
          )}
        />
      </WorkspaceCardValue>
    </WorkspaceCard>
  )
}

export { LeadOverviewActivityCard }
